import { useCallback, useEffect, memo } from "react";
import type { Dispatch, SetStateAction } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  Image,
} from "react-native";
import { useDebounced } from "../../../../../utils/useDebounced";
import { trackEvent } from "../../../../../utils/mixpanel";
import airtableUtils from "../../../../../services/airtableUtils";
import { RainbowBorder, NormalText } from "../../../../common/native";
import { TOOLTIP_STEPS } from "../../../../../utils/constant";
import type { TTooltipStep } from "../../../../../utils/constant";
import type { TConsultView, TSession } from "../../../../../utils/types";
import { enrichPatientsWithProfileImages } from "../services/profileImageService";
import styles from "./ConsultSearch.styles";

export interface ConsultPatient {
  id?: string;
  name: string;
  appointmentTime?: string;
  visitType?: string;
  email?: string;
  phone?: string;
  patientSummary?: string;
  hasPhotos?: boolean;
  procedureType?: string;
  consultationDate?: string;
  clinicOSUUID?: string;
  patientRecordId?: string;
  photos?: { imageUrl: string }[];
  profileImageUrl?: string | null;
  [key: string]: any;
}

export interface ConsultSection {
  date: string;
  title?: string;
  data: ConsultPatient[];
  error?: string;
}

interface SearchProps {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  setSearchMode: Dispatch<SetStateAction<boolean>>;
  setSearchResults: Dispatch<SetStateAction<ConsultSection[]>>;
  session: TSession | null;
  currentView: TConsultView;
  onPatientSelect: (patient: ConsultPatient) => void;
  tipsEnabled: boolean;
  tipsState: Partial<Record<TTooltipStep, boolean>>;
}

/**
 * Manages search functionality
 */
export const useConsultSearch = ({
  searchTerm,
  setSearchTerm,
  setIsSearching,
  setSearchMode,
  setSearchResults,
  session,
  currentView,
  onPatientSelect,
  tipsEnabled,
  tipsState,
}: SearchProps) => {
  const { width } = useWindowDimensions();
  const isDesktopLayout = width >= 1100;

  // Run a patient search for the given term.
  const runSearch = useCallback(
    async (term: string) => {
      const query = term.trim();
      if (!query || query.length === 1) {
        setSearchResults([]);
        setSearchMode(false);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      setSearchMode(true);

      const clinicId = session?.clinicId;
      if (!clinicId) {
        setSearchResults([
          {
            date: "session-warning",
            title: "Session Required",
            data: [],
            error:
              "Your session has expired—please log in again to search patients.",
          },
        ]);
        setIsSearching(false);
        return;
      }

      trackEvent(
        "Searched",
        { "Search Term": query, "View Mode": currentView },
        session ?? undefined,
      );

      try {
        const results = await airtableUtils.searchPatientsClinicOS(
          query,
          clinicId,
          session,
        );

        trackEvent(
          "Search Results",
          {
            "Search Term": query,
            "Results Count": results.length,
            "View Mode": currentView,
          },
          session ?? undefined,
        );

        if (results.length > 0) {
          const searchPatients = results.map((patient) => ({
            ...patient,
            visitType: "Patient Record",
          }));
          const patientsWithProfileImages =
            await enrichPatientsWithProfileImages(searchPatients, session);

          setSearchResults([
            {
              date: "search-results",
              title: "Search Results",
              data: patientsWithProfileImages,
            },
          ]);
        } else {
          setSearchResults([
            { date: "no-results", title: "No Results", data: [] },
          ]);
        }
      } catch (error) {
        console.error("Error performing search:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to search patients";
        setSearchResults([
          {
            date: "error",
            title: "Search Error",
            data: [],
            error: errorMessage,
          },
        ]);
      } finally {
        setIsSearching(false);
      }
    },
    [session, currentView, setSearchResults, setSearchMode, setIsSearching],
  );

  // Auto-search as the user types, debounced so we don't query per keystroke.
  const debouncedSearchTerm = useDebounced(searchTerm, 350);
  useEffect(() => {
    runSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, runSearch]);

  // Handle search term change
  const handleSearchTermChange = useCallback(
    (text: string) => {
      setSearchTerm(text);
    },
    [setSearchTerm],
  );

  // Clear search
  const clearSearch = useCallback(() => {
    trackEvent(
      "Clear Search",
      { "Search Term": searchTerm, "Results Count": 0 },
      session ?? undefined,
    );
    setSearchTerm("");
    setSearchResults([]);
    setSearchMode(false);
    setIsSearching(false);
  }, [
    searchTerm,
    session,
    setSearchTerm,
    setSearchResults,
    setSearchMode,
    setIsSearching,
  ]);

  // Render section header
  const renderSectionHeader = useCallback(
    ({ section }: { section: ConsultSection }) => {
      if (section.date === "session-warning") {
        return (
          <View style={styles.sessionWarningContainer}>
            <View style={styles.sessionWarningHeader}>
              <Text style={styles.sessionWarningTitle}>
                {section.title || "Session Required"}
              </Text>
              <Text style={styles.sessionWarningText}>
                {section.error ||
                  "We need you to log in again before searching for patients."}
              </Text>
            </View>
            <TouchableOpacity
              style={styles.clearSearchButton}
              onPress={clearSearch}
            >
              <Text style={styles.clearSearchText}>Clear Search</Text>
            </TouchableOpacity>
          </View>
        );
      }

      if (section.date === "search-results") {
        return (
          <View style={styles.searchResultsHeader}>
            <View style={styles.searchResultsInfo}>
              <Text style={styles.searchResultsTitle}>
                {section.data.length === 0
                  ? "Searching..."
                  : `${section.data.length} ${section.data.length === 1 ? "Patient" : "Patients"} Found`}
              </Text>
              <Text style={styles.searchResultsSubtitle}>
                Search results for "{searchTerm}"
              </Text>
            </View>
            <TouchableOpacity
              style={styles.clearSearchButton}
              onPress={clearSearch}
            >
              <Text style={styles.clearSearchText}>Clear</Text>
            </TouchableOpacity>
          </View>
        );
      }

      if (section.date === "no-results") {
        return (
          <View style={styles.noResultsContainer}>
            <View style={styles.noResultsHeader}>
              <Text style={styles.noResultsTitle}>No patients found</Text>
              <Text style={styles.noResultsText}>
                No patients found matching "{searchTerm}"
              </Text>
              <Text style={styles.noResultsSuggestion}>
                Try a different search or check the spelling
              </Text>
            </View>
            <TouchableOpacity
              style={styles.clearSearchButton}
              onPress={clearSearch}
            >
              <Text style={styles.clearSearchText}>Clear Search</Text>
            </TouchableOpacity>
          </View>
        );
      }

      if (section.date === "error") {
        return (
          <View style={styles.errorContainer}>
            <View style={styles.errorHeader}>
              <Text style={styles.errorTitle}>Search Error</Text>
              <Text style={styles.errorText}>
                {section.error || "Error searching for patients"}
              </Text>
              <TouchableOpacity
                style={styles.retryButton}
                onPress={() => runSearch(searchTerm)}
              >
                <Text style={styles.retryButtonText}>Try Again</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity
              style={styles.clearSearchButton}
              onPress={clearSearch}
            >
              <Text style={styles.clearSearchText}>Clear Search</Text>
            </TouchableOpacity>
          </View>
        );
      }

      // Regular date section header
      return (
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderText}>
            {/* Format date here */}
            {section.title || section.date}
          </Text>
          <View style={styles.sectionHeaderDivider} />
        </View>
      );
    },
    [searchTerm, clearSearch, runSearch],
  );

  // Memoized patient item component to prevent re-renders when only profileImageUrl changes
  const PatientItem = memo(
    ({
      item,
      isSearchResult,
      isDesktopLayout,
      onPress,
      tipsEnabled,
      tipsState,
    }: {
      item: ConsultPatient;
      isSearchResult: boolean;
      isDesktopLayout: boolean;
      onPress: () => void;
      tipsEnabled: boolean;
      tipsState: Partial<Record<TTooltipStep, boolean>>;
    }) => {
      const patientSummaryText = item.patientSummary?.trim() ?? "";
      const avatarInitial = (item.name?.trim()?.charAt(0) || "?").toUpperCase();
      const [timeValue = "", meridiem = ""] = item.appointmentTime
        ? item.appointmentTime.split(" ")
        : ["--:--", ""];

      const itemContent = (
        <View
          style={[
            styles.patientListItem,
            isSearchResult && styles.searchResultItem,
          ]}
        >
          <View style={styles.timeColumn}>
            {isSearchResult ? (
              <Text style={styles.searchResultTimeText}>Patient</Text>
            ) : (
              <>
                <Text style={styles.timeText}>{timeValue}</Text>
                <Text style={styles.amPmText}>{meridiem}</Text>
              </>
            )}
          </View>

          <View
            style={[
              styles.patientInfoColumn,
              isDesktopLayout && styles.patientInfoColumnDesktop,
            ]}
          >
            <View
              style={[
                styles.patientInfoContent,
                isDesktopLayout && styles.patientInfoContentDesktop,
              ]}
            >
              <View
                style={[
                  styles.patientPrimaryInfo,
                  isDesktopLayout && styles.patientPrimaryInfoDesktop,
                ]}
              >
                <View style={styles.patientNameRow}>
                  <View style={styles.avatarContainer}>
                    {item.profileImageUrl ? (
                      <Image
                        source={{ uri: item.profileImageUrl }}
                        style={styles.avatarImage}
                      />
                    ) : (
                      <Text style={styles.avatarText}>{avatarInitial}</Text>
                    )}
                  </View>
                  <View>
                    <Text style={styles.patientName}>{item.name}</Text>
                    {isSearchResult && (
                      <Text style={styles.searchResultLabel}>From Search</Text>
                    )}
                  </View>
                </View>
                <Text style={styles.procedureType}>
                  {isSearchResult
                    ? "Patient Record - Schedule Consultation"
                    : item.visitType}
                </Text>

                {isSearchResult && (
                  <View style={styles.contactInfo}>
                    {item.email && (
                      <Text
                        style={styles.contactText}
                      >{`📧 ${item.email}`}</Text>
                    )}
                    {item.phone && (
                      <Text
                        style={styles.contactText}
                      >{`📞 ${item.phone}`}</Text>
                    )}
                  </View>
                )}
              </View>

              {!!patientSummaryText && (
                <View
                  style={[
                    styles.patientSummaryContainer,
                    isDesktopLayout && styles.patientSummaryContainerDesktop,
                  ]}
                >
                  <View style={styles.summaryBox}>
                    <NormalText
                      text={patientSummaryText}
                      style={styles.summaryText}
                    />
                  </View>
                </View>
              )}
            </View>
          </View>
        </View>
      );

      return (
        <RainbowBorder
          isActive={
            tipsEnabled &&
            !isSearchResult &&
            tipsState[TOOLTIP_STEPS.SELECT_APPOINTMENT] &&
            !tipsState[TOOLTIP_STEPS.VIEW_APPOINTMENT_DETAIL]
          }
          borderRadius={10}
        >
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={onPress}
            style={{ width: "100%" }}
            testID={`consult-patient-row-${item.id ?? item.name ?? "unknown"}`}
          >
            {itemContent}
          </TouchableOpacity>
        </RainbowBorder>
      );
    },
    (prevProps, nextProps) => {
      // Only re-render if these specific props change
      return (
        prevProps.item.id === nextProps.item.id &&
        prevProps.item.name === nextProps.item.name &&
        prevProps.item.profileImageUrl === nextProps.item.profileImageUrl &&
        prevProps.item.appointmentTime === nextProps.item.appointmentTime &&
        prevProps.item.patientSummary === nextProps.item.patientSummary &&
        prevProps.isSearchResult === nextProps.isSearchResult &&
        prevProps.isDesktopLayout === nextProps.isDesktopLayout &&
        prevProps.tipsEnabled === nextProps.tipsEnabled &&
        prevProps.tipsState === nextProps.tipsState
      );
    },
  );

  // Render patient item
  const renderPatientItem = useCallback(
    ({ item, section }: { item: ConsultPatient; section: ConsultSection }) => {
      const isSearchResult = section?.date === "search-results";

      return (
        <PatientItem
          item={item}
          isSearchResult={isSearchResult}
          isDesktopLayout={isDesktopLayout}
          onPress={() => onPatientSelect(item)}
          tipsEnabled={tipsEnabled}
          tipsState={tipsState}
        />
      );
    },
    [isDesktopLayout, onPatientSelect, tipsEnabled, tipsState],
  );

  return {
    handleSearchTermChange,
    clearSearch,
    renderSectionHeader,
    renderPatientItem,
  };
};
